/**
 * 주소록 API 클라이언트 (migration 171, 2026-09-08).
 *
 * 서버 `apps/chat-server/src/modules/contacts/contacts.routes.ts` 와 1:1.
 *
 * ★★★ **차단은 주소록 API 가 아니다.** 「차단」은 메일의 수신거부목록
 *    (`POST /mail/address-rules {kind:'BLOCK'}`)을 그대로 부른다 — 그 표가 이미 있고
 *    **실제로 스팸 배치를 걸고 있다**(`resolveImportPlacement`). 여기에 따로 두면 차단이
 *    두 곳으로 갈려, 환경설정에서 풀어도 주소록이 계속 막는 상태가 된다.
 */

import { apiClient } from './client';

export type ContactScope = 'PERSONAL' | 'SHARED';

export interface ContactTag {
  id: string;
  name: string;
  color: string | null;
}

export interface Contact {
  id: string;
  ownerId: string;
  scope: ContactScope;
  /** 비어 있으면 화면이 「(이름없음)」으로 그린다 — 빈 칸이 아니라 값이다. */
  name: string | null;
  email: string;
  phone: string | null;
  mobile: string | null;
  company: string | null;
  department: string | null;
  position: string | null;
  note: string | null;
  /**
   * 명함 사진의 파일 번호 (2026-10-06 · DB `contacts.photo_file_id` bigint NULL · 마이그 224).
   *
   * ⛔ **선택 칸이다 — `string | null` 로 좁히지 말 것.** 마이그 224 가 아직 «안 걸렸고»
   *    서버도 안 올라가서, 오늘 운영 응답에는 **이 열쇠 자체가 없다**. 선택으로 두면
   *    `undefined` 가 와서 화면이 종전대로 이니셜을 그린다. `string | null` 로 적으면
   *    「서버가 null 을 줬다(사진 없음)」와 「서버가 모른다(옛 서버)」가 한 값으로 뭉개진다.
   * ⚠ 서버는 bigint 를 **문자열로** 흘린다 — 숫자로 받지 말 것(관제 2026-10-06).
   * ⚠ 올리기·지우기는 이 칸으로 하지 않는다. 그 끝점은 아직 없다(지시 6 · 별도 판).
   */
  photoFileId?: string | null;
  isStarred: boolean;
  tags: ContactTag[];
  createdAt: string;
  updatedAt: string;
}

export interface ContactListParams {
  /** 'ALL' 은 개인 ∪ 공유 — 「중요 주소록」이 scope 를 가로지르기 때문에 쓴다. */
  scope?: ContactScope | 'ALL';
  tagId?: string;
  starred?: boolean;
  q?: string;
  sort?: 'name' | 'email' | 'recent';
  limit?: number;
  offset?: number;
}

export interface ContactListResponse {
  contacts: Contact[];
  total: number;
  limit: number;
  offset: number;
}

export interface ContactInput {
  scope?: ContactScope;
  email: string;
  name?: string | null;
  phone?: string | null;
  mobile?: string | null;
  company?: string | null;
  department?: string | null;
  position?: string | null;
  note?: string | null;
  isStarred?: boolean;
  tagIds?: string[];
}

/**
 * 고칠 때만 보낼 수 있는 칸까지 더한 꼴 (2026-10-06 지시 6).
 *
 * ⛔⛔ **`photoFileId` 를 `ContactInput` 에 넣지 않았다 — POST 가 그 칸을 «모른다».**
 *    서버 `POST /contacts` 본문 스키마에 `photoFileId` 가 **0건**이고, 그 스키마는
 *    `additionalProperties:false` 다. ⇒ 담을 때 보내면 **400 이 아니라 «조용히 지워진다»**
 *    (ajv 의 removeAdditional). 「담았는데 사진이 안 붙었다」가 오류 없이 생긴다.
 * ⇒ ★ 그래서 담기(`create`)와 고치기(`update`)의 꼴을 갈랐다.
 *
 * ⭐ 서버 `PATCH` 는 이 칸을 **세 갈래**로 읽는다 — 그 셋이 아래 세 값과 1:1 이다:
 * ```
 * 칸을 «안 보냄»(undefined) ⇒ 사진을 «안 건드린다»
 * null                      ⇒ 사진을 «뗀다»
 * '숫자 글자'               ⇒ 그 파일을 «붙인다» (ajv pattern ^[0-9]{1,20}$ · 아니면 400)
 * ```
 */
export interface ContactUpdateInput extends Partial<ContactInput> {
  photoFileId?: string | null;
}

/** 골라 담기(E1-4) 한 줄 — 표에서 그 자리에 고친 값이 그대로 온다. */
export interface ContactBulkItem {
  email: string;
  name?: string | null;
  mobile?: string | null;
  tagIds?: string[];
}

export interface ContactBulkResult {
  added: number;
  addedEmails: string[];
  /** 못 담은 줄 — 「이미 있음」과 「형식이 틀림」을 가른다. 판을 물리지 않기 때문에 함께 온다. */
  skipped: Array<{ email: string; reason: 'DUPLICATE' | 'INVALID' }>;
}

export interface ContactLookupResult {
  /** 주소 → 담긴 연락처. 없으면 키 자체가 없다. */
  contacts: Record<string, { id: string; scope: ContactScope; name: string | null }>;
  /** 막아 둔 주소들(수신거부목록). */
  blocked: string[];
}

export const contactsApi = {
  async list(params: ContactListParams = {}): Promise<ContactListResponse> {
    const { data } = await apiClient.get<ContactListResponse>('/contacts', {
      params: {
        scope: params.scope,
        tagId: params.tagId,
        // 서버 스키마가 문자열 enum 이라 boolean 을 그대로 보내지 않는다.
        starred: params.starred ? 'true' : undefined,
        q: params.q || undefined,
        sort: params.sort,
        limit: params.limit != null ? String(params.limit) : undefined,
        offset: params.offset != null ? String(params.offset) : undefined,
      },
    });
    return data;
  },

  async create(input: ContactInput): Promise<Contact> {
    const { data } = await apiClient.post<{ contact: Contact }>('/contacts', input);
    return data.contact;
  },

  async bulkCreate(items: ContactBulkItem[], scope: ContactScope = 'PERSONAL'): Promise<ContactBulkResult> {
    const { data } = await apiClient.post<ContactBulkResult>('/contacts/bulk', { scope, items });
    return data;
  },

  async update(id: string, input: ContactUpdateInput): Promise<Contact> {
    const { data } = await apiClient.patch<{ contact: Contact }>(`/contacts/${id}`, input);
    return data.contact;
  },

  async remove(id: string): Promise<void> {
    await apiClient.delete(`/contacts/${id}`);
  },

  /**
   * 이 주소들이 담겼나 · 막혔나 — 메일 화면이 「담기 단추냐 배지냐」를 가르는 데 쓴다.
   * ★ 한 번에 묻는다. 두 번 왕복하면 화면이 반쯤 그려진 채로 한 박자 늦게 바뀐다.
   */
  async lookup(emails: string[]): Promise<ContactLookupResult> {
    const list = [...new Set(emails.map((e) => e.trim().toLowerCase()).filter(Boolean))];
    if (list.length === 0) return { contacts: {}, blocked: [] };
    const { data } = await apiClient.get<ContactLookupResult>('/contacts/lookup', {
      params: { emails: list.join(',') },
    });
    return data;
  },

  async listTags(): Promise<ContactTag[]> {
    const { data } = await apiClient.get<{ tags: ContactTag[] }>('/contacts/tags');
    return data.tags;
  },

  async createTag(name: string, color?: string | null): Promise<ContactTag> {
    const { data } = await apiClient.post<{ tag: ContactTag }>('/contacts/tags', { name, color });
    return data.tag;
  },

  async removeTag(id: string): Promise<void> {
    await apiClient.delete(`/contacts/tags/${id}`);
  },
};

/**
 * 「차단」의 결과 — 화면이 무엇을 말해야 하는지가 갈린다.
 *
 * ★ 서버는 **이미 있으면 409** 다(`RULE_EXISTS`). 그것을 실패로 그리면 사용자는
 *   「차단이 안 됐다」고 읽는데, 실제로는 **이미 막혀 있다**. 「됐다」와 「이미 그렇다」는
 *   둘 다 원하는 상태라 함께 묶고, 손댈 곳이 있는 둘(허용목록에 있음 · 내 주소)만 가른다.
 */
export type BlockResult = 'BLOCKED' | 'ALREADY' | 'IN_ALLOW_LIST' | 'SELF' | 'FAILED';

/** 메일의 수신거부목록에 주소를 넣는다(위 파일 머리 참조). */
export async function blockAddress(address: string): Promise<BlockResult> {
  try {
    await apiClient.post('/mail/address-rules', {
      kind: 'BLOCK', address: address.trim().toLowerCase(),
    });
    return 'BLOCKED';
  } catch (err) {
    const e = err as { response?: { status?: number; data?: { code?: string } } };
    const code = e.response?.data?.code;
    if (code === 'RULE_EXISTS') return 'ALREADY';
    if (code === 'RULE_CONFLICT') return 'IN_ALLOW_LIST';
    if (code === 'SELF_ADDRESS') return 'SELF';
    return 'FAILED';
  }
}
